import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataContext } from '../../../../context/DataContext';
import { PageContainer, Section, StatGrid } from '../../components/AdminContentWrapper';
import RequireBillingRole from './components/RequireBillingRole';
import ConsumptionBucketsTable from './components/ConsumptionBucketsTable';
import CreditsEngineService, { formatTransactionType, toNumber } from '../../../../services/CreditsEngineService';
import TablePaginationBar from '../../../../components/common/TablePaginationBar';
import { useTablePagination } from '../../../../hooks';
import HelpIcon from '../../../../components/help/HelpIcon';

const darkTextFieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'var(--wb-text-strong)',
    '& fieldset': { borderColor: 'var(--wb-outline)' },
    '&:hover fieldset': { borderColor: 'var(--wb-text-dim)' }
  },
  '& .MuiInputLabel-root': { color: 'var(--wb-text-soft)' }
};

const cellSx = { color: 'var(--wb-text)', borderColor: 'var(--wb-border)' };
const headSx = { ...cellSx, color: 'var(--wb-muted-85)', fontWeight: 600 };

const BillingTenantAccountContent = () => {
  const { accountId: ownAccountId, isPlatformRoot } = useContext(DataContext);

  const [targetAccountId, setTargetAccountId] = useState(ownAccountId || '');
  useEffect(() => {
    if (ownAccountId && !targetAccountId) {
      setTargetAccountId(ownAccountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownAccountId]);

  const [wallet, setWallet] = useState(null);
  const [limits, setLimits] = useState(null);
  const [usage, setUsage] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [ledgerCursor, setLedgerCursor] = useState(null);
  const [isLoadingMoreLedger, setIsLoadingMoreLedger] = useState(false);
  const [buckets, setBuckets] = useState([]);
  const [period, setPeriod] = useState('day');

  const [isLoadingWallet, setIsLoadingWallet] = useState(false);
  const [isLoadingLedger, setIsLoadingLedger] = useState(false);
  const [isLoadingConsumption, setIsLoadingConsumption] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [isAdjusting, setIsAdjusting] = useState(false);

  const activeAccountId = targetAccountId.trim();

  const loadWallet = useCallback(async () => {
    if (!activeAccountId) return;
    try {
      setIsLoadingWallet(true);
      setError(null);
      const [balanceData, limitsData, usageData] = await Promise.all([
        CreditsEngineService.getBalance({ accountId: activeAccountId }),
        CreditsEngineService.getLimits({ accountId: activeAccountId }),
        CreditsEngineService.getUsage({ accountId: activeAccountId })
      ]);
      setWallet(balanceData);
      setLimits(limitsData);
      setUsage(usageData);
    } catch (err) {
      setError(err.message || 'No fue posible obtener el saldo del tenant.');
    } finally {
      setIsLoadingWallet(false);
    }
  }, [activeAccountId]);

  // Paginación con cursor del backend: la primera carga trae una página y
  // `loadMore` va apilando las siguientes. El pager numerado opera sobre lo
  // acumulado; useTablePagination llama a `loadMore` cuando el usuario
  // entra a la última página cargada y todavía queda cursor.
  const loadLedger = useCallback(async () => {
    if (!activeAccountId) return;
    try {
      setIsLoadingLedger(true);
      const { events, nextCursor } = await CreditsEngineService.getLedger({
        accountId: activeAccountId, limit: 50
      });
      setLedger(events);
      setLedgerCursor(nextCursor);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No fue posible obtener el historial de transacciones.', severity: 'error' });
    } finally {
      setIsLoadingLedger(false);
    }
  }, [activeAccountId]);

  const loadMoreLedger = useCallback(async () => {
    if (!activeAccountId || !ledgerCursor || isLoadingMoreLedger) return;
    setIsLoadingMoreLedger(true);
    try {
      const { events, nextCursor } = await CreditsEngineService.getLedger({
        accountId: activeAccountId, limit: 50, cursor: ledgerCursor
      });
      setLedger((prev) => [...prev, ...events]);
      setLedgerCursor(nextCursor);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No fue posible obtener más transacciones.', severity: 'error' });
    } finally {
      setIsLoadingMoreLedger(false);
    }
  }, [activeAccountId, ledgerCursor, isLoadingMoreLedger]);

  const loadConsumption = useCallback(async () => {
    if (!activeAccountId) return;
    try {
      setIsLoadingConsumption(true);
      const items = await CreditsEngineService.getUsageBuckets({ accountId: activeAccountId, bucket: period });
      setBuckets(items);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No fue posible obtener el consumo del tenant.', severity: 'error' });
    } finally {
      setIsLoadingConsumption(false);
    }
  }, [activeAccountId, period]);

  const loadAll = useCallback(() => {
    loadWallet();
    loadLedger();
    loadConsumption();
  }, [loadWallet, loadLedger, loadConsumption]);

  useEffect(() => {
    if (activeAccountId) {
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountId]);

  useEffect(() => {
    if (activeAccountId) {
      loadConsumption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const statItems = useMemo(() => {
    if (!wallet) return [];
    return [
      { label: 'Saldo disponible (créditos)', value: toNumber(wallet.available_credits).toFixed(2) },
      { label: 'Reservado (créditos)', value: toNumber(wallet.reserved_credits).toFixed(2) },
      { label: 'Saldo total (créditos)', value: toNumber(wallet.balance_credits).toFixed(2) }
    ];
  }, [wallet]);

  const usageItems = useMemo(() => {
    if (!usage) return [];
    return [
      { label: 'Créditos consumidos (período)', value: toNumber(usage.total_credits_consumed).toFixed(2) },
      { label: 'Costo interno (período)', value: `$${toNumber(usage.total_internal_cost).toFixed(4)}` },
      { label: 'Invocaciones (período)', value: String(usage.invocation_count ?? 0) }
    ];
  }, [usage]);

  const handleAdjustSubmit = async () => {
    if (!adjustAmount || Number.isNaN(Number(adjustAmount)) || Number(adjustAmount) === 0) {
      setSnackbar({ open: true, message: 'Ingresa un monto de créditos distinto de cero.', severity: 'error' });
      return;
    }
    if (!adjustReason.trim()) {
      setSnackbar({ open: true, message: 'El motivo del ajuste es requerido (queda auditado en el ledger).', severity: 'error' });
      return;
    }
    try {
      setIsAdjusting(true);
      await CreditsEngineService.adjustBalance({
        account_id: activeAccountId,
        amount: Number(adjustAmount),
        reason: adjustReason.trim()
      });
      setSnackbar({ open: true, message: 'Saldo ajustado correctamente.', severity: 'success' });
      setAdjustOpen(false);
      setAdjustAmount('');
      setAdjustReason('');
      loadWallet();
      loadLedger();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No se pudo ajustar el saldo.', severity: 'error' });
    } finally {
      setIsAdjusting(false);
    }
  };

  const { pageRows, paginationProps } = useTablePagination(ledger, {
    storageKey: 'billingTenantLedger',
    hasMore: Boolean(ledgerCursor),
    isLoadingMore: isLoadingMoreLedger,
    onNeedMore: loadMoreLedger
  });

  return (
    <PageContainer
      title="Mi cuenta y créditos"
      description="Saldo de créditos del tenant, límites, historial de transacciones y consumo hora/día/mes."
      actions={
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadAll} disabled={!activeAccountId}
          sx={{ borderColor: 'var(--wb-blue-40)', color: 'var(--wb-blue)', '&:hover': { borderColor: 'var(--wb-blue)' } }}>
          Actualizar
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error al cargar datos</AlertTitle>
          {error}
        </Alert>
      )}

      {isPlatformRoot && (
        <Section title="Tenant a inspeccionar" subtitle="Como root, puedes ver cualquier tenant escribiendo su account_id.">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <TextField
              label="account_id"
              value={targetAccountId}
              onChange={(e) => setTargetAccountId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadAll()}
              size="small"
              sx={{ flex: 1, ...darkTextFieldStyle }}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="cuenta-y-creditos" anchor="account_id-solo-root-tenant-a-inspeccionar" /></InputAdornment>
              ) }}
            />
            <Button variant="contained" onClick={loadAll} disabled={!activeAccountId} sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}>
              Cargar
            </Button>
          </Stack>
        </Section>
      )}

      {!activeAccountId ? (
        <Alert severity="info">
          {isPlatformRoot
            ? 'Ingresa un account_id para ver su información de créditos.'
            : 'No pudimos determinar tu account_id. Verifica tu sesión.'}
        </Alert>
      ) : (
        <>
          <Section
            title="Saldo del tenant"
            subtitle={`account_id: ${activeAccountId}`}
            chips={isLoadingWallet ? ['Actualizando…'] : undefined}
          >
            {isLoadingWallet && !wallet ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
              </Box>
            ) : (
              <>
                <StatGrid items={statItems} />
                {isPlatformRoot && (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 2 }}>
                    <Button variant="outlined" onClick={() => setAdjustOpen(true)}
                      sx={{ borderColor: 'var(--wb-blue-40)', color: 'var(--wb-blue)', '&:hover': { borderColor: 'var(--wb-blue)' } }}>
                      Ajustar saldo
                    </Button>
                    <HelpIcon module="creditos" topic="cuenta-y-creditos" anchor="ajustar-saldo-solo-root" />
                  </Stack>
                )}
              </>
            )}
          </Section>

          <Section title="Uso (resumen del mes en curso)" subtitle="Ver 'Consumo del tenant' abajo para el desglose por período.">
            {isLoadingWallet && !usage ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
              </Box>
            ) : (
              <StatGrid items={usageItems} />
            )}
          </Section>

          <Section title="Límites" subtitle="Editables desde 'Política de crédito'.">
            {isLoadingWallet && !limits ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
              </Box>
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                <Box>
                  <Typography sx={{ color: 'var(--wb-muted-85)', fontWeight: 600, mb: 0.5 }}>Cuenta</Typography>
                  <Typography sx={{ color: 'var(--wb-text)' }}>
                    Mensual: {limits?.account_policy?.monthly_credit_limit != null ? toNumber(limits.account_policy.monthly_credit_limit).toFixed(2) : 'Sin límite'}
                  </Typography>
                  <Typography sx={{ color: 'var(--wb-text)' }}>
                    Diario: {limits?.account_policy?.daily_credit_limit != null ? toNumber(limits.account_policy.daily_credit_limit).toFixed(2) : 'Sin límite'}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: 'var(--wb-muted-85)', fontWeight: 600, mb: 0.5 }}>Tu usuario</Typography>
                  <Typography sx={{ color: 'var(--wb-text)' }}>
                    Mensual: {limits?.user_policy?.monthly_credit_limit != null ? toNumber(limits.user_policy.monthly_credit_limit).toFixed(2) : 'Sin límite'}
                  </Typography>
                  <Typography sx={{ color: 'var(--wb-text)' }}>
                    Diario: {limits?.user_policy?.daily_credit_limit != null ? toNumber(limits.user_policy.daily_credit_limit).toFixed(2) : 'Sin límite'}
                  </Typography>
                </Box>
              </Stack>
            )}
          </Section>

          <Section title="Historial de transacciones" subtitle="Últimos 50 eventos del ledger de este tenant.">
            {isLoadingLedger ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
              </Box>
            ) : ledger.length === 0 ? (
              <Typography sx={{ color: 'var(--wb-muted-70)', py: 2, textAlign: 'center' }}>
                Todavía no hay transacciones registradas para este tenant.
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Fecha</TableCell>
                      <TableCell sx={headSx}>Tipo</TableCell>
                      <TableCell sx={headSx} align="right">Monto</TableCell>
                      <TableCell sx={headSx} align="right">Saldo resultante</TableCell>
                      <TableCell sx={headSx}>Operación</TableCell>
                      <TableCell sx={headSx}>Referencia</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageRows.map((event) => {
                      const typeInfo = formatTransactionType(event.transaction_type);
                      return (
                        <TableRow key={event.event_id || `${event.created_at}-${event.reference_id}`} hover>
                          <TableCell sx={cellSx}>{event.created_at || '—'}</TableCell>
                          <TableCell sx={cellSx}>
                            <Chip size="small" label={typeInfo.label} color={typeInfo.color} variant="outlined" />
                          </TableCell>
                          <TableCell sx={cellSx} align="right">{toNumber(event.amount).toFixed(4)}</TableCell>
                          <TableCell sx={cellSx} align="right">
                            {event.balance_after != null ? toNumber(event.balance_after).toFixed(4) : '—'}
                          </TableCell>
                          <TableCell sx={cellSx}>{event.operation_type || '—'}</TableCell>
                          <TableCell sx={cellSx}>{event.reference_id || '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {ledger.length > 0 && <TablePaginationBar {...paginationProps} />}
          </Section>

          <Section title="Consumo del tenant" subtitle="Agregado por hora, día o mes.">
            <FormControl size="small" sx={{ minWidth: 160, mb: 2 }}>
              <InputLabel sx={{ color: 'var(--wb-text-soft)' }}>Período</InputLabel>
              <Select value={period} label="Período" onChange={(e) => setPeriod(e.target.value)} sx={{ color: 'var(--wb-text-strong)' }}>
                <MenuItem value="hour">Por hora</MenuItem>
                <MenuItem value="day">Por día</MenuItem>
                <MenuItem value="month">Por mes</MenuItem>
              </Select>
            </FormControl>
            <ConsumptionBucketsTable buckets={buckets} isLoading={isLoadingConsumption} />
          </Section>
        </>
      )}

      {/* Ajustar saldo (root únicamente) */}
      <Dialog open={adjustOpen} onClose={() => !isAdjusting && setAdjustOpen(false)} PaperProps={{ sx: { backgroundColor: 'var(--wb-bg-surface-alt)', color: 'var(--wb-text)', minWidth: { xs: '90%', sm: 420 } } }}>
        <DialogTitle>Ajustar saldo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography sx={{ color: 'var(--wb-muted-85)' }}>
              Ingresa un monto de créditos (positivo para acreditar, negativo para corregir un exceso). Queda auditado
              permanentemente en el historial de transacciones.
            </Typography>
            <TextField
              label="Monto de créditos"
              value={adjustAmount}
              onChange={(e) => setAdjustAmount(e.target.value)}
              type="number"
              inputProps={{ step: '0.01' }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
            />
            <TextField
              label="Motivo (obligatorio)"
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
              placeholder="Ej. corrección de cobro duplicado del 2026-08-20"
              fullWidth
              multiline
              minRows={2}
              size="small"
              sx={darkTextFieldStyle}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustOpen(false)} disabled={isAdjusting}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAdjustSubmit}
            disabled={isAdjusting}
            startIcon={isAdjusting ? <CircularProgress color="inherit" size={16} /> : null}
            sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}
          >
            Aplicar ajuste
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor:
              snackbar.severity === 'success' ? 'var(--wb-success-90)'
                : snackbar.severity === 'warning' ? 'var(--wb-warning-90)'
                  : 'var(--wb-danger-95)'
          }
        }}
      />
    </PageContainer>
  );
};

const BillingTenantAccount = () => (
  <RequireBillingRole allow="root-or-admin" title="Mi cuenta y créditos">
    <BillingTenantAccountContent />
  </RequireBillingRole>
);

export default BillingTenantAccount;
